import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import * as Yup from 'yup';
import ScrollToTop from './components/scroll-to-top/ScrollToTop';
import Router from './routes';
import ThemeProvider from './theme';
import axios from 'axios';
import { INIT_TOKEN_SAGA, authActions } from './store/authSlice';
import { store } from './store/configureStore';
import _ from 'lodash';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const { auth } = store.getState();

    const refreshToken = auth.refreshToken;

    const originalRequest = error.config;
    const isJwtError =
      _.get(error, 'response.data.response.data.message') ===
      'Token is expired';
    if (isJwtError && !!refreshToken && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        axios
          .put('/api/v1/auth/tokens', { refreshToken })
          .then(({ data }) => {
            const body = data?.data;
            originalRequest.headers['Authorization'] =
              'Bearer ' + body?.accessToken;

            const action = authActions.changeTokens({
              accessToken: body?.accessToken,
              refreshToken: body?.refreshToken,
            });
            store.dispatch(action);

            processQueue(null, body?.accessToken);
            resolve(axios(originalRequest));
          })
          .catch(err => {
            processQueue(err, null);
            reject(err);

            store.dispatch({ type: INIT_TOKEN_SAGA });
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  },
);

axios.interceptors.request.use(function (config) {
  // Attach access token only when host is same
  if (_.startsWith(config.url, '/api')) {
    const { accessToken } = JSON.parse(
      localStorage.getItem('persist:auth') || JSON.stringify({}),
    );
    if (!!accessToken) {
      config.headers.Authorization =
        config.headers.Authorization || `Bearer ${accessToken}`;
    }
  }
  return config;
});

function isNumberErrorWhenDefined(this: any) {
  return this.transform((v: any) => (v === '' ? undefined : v))
    .nullable()
    .test({
      message: '숫자만 입력해주세요.',
      name: 'isNumberErrorWhenDefined',
      test(value: any) {
        if (value === undefined || value === null || value === '') {
          return true;
        }
        if (typeof +value !== 'number' && !isNaN(+value)) {
          return false;
        }
        return true;
      },
    });
}

// Add the custom validation method to Yup
Yup.addMethod(Yup.mixed, 'isNumberErrorWhenDefined', isNumberErrorWhenDefined);

function App() {
  return (
    <>
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <ScrollToTop />
            <Router />
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </>
  );
}

export default App;
